/**
 * Formats a currency amount with appropriate suffix (K, M, B)
 * @param amount - The amount to format (can be null for optional amounts)
 * @returns Formatted string like "$5M" or "$100K", empty string if null
 */
export function formatAmount(amount: number | null): string {
  if (amount === null) return '';
  if (amount >= 1000000000) {
    return `$${(amount / 1000000000).toFixed(1)}B`;
  }
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount}`;
}

/**
 * Formats a currency amount (non-null version for display)
 * @param amount - The amount to format
 * @returns Formatted string like "$5M" or "$100K"
 */
export function formatCurrency(amount: number): string {
  return formatAmount(amount) || '$0';
}

export function formatTimeAgo(input: Date | string | number): string {
  const then = new Date(input).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) {
    return "Just now";
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  }

  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) {
    return diffHrs === 1 ? "1 hour ago" : `${diffHrs} hours ago`;
  }

  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    return diffWeeks === 1 ? "Last week" : `${diffWeeks} weeks ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths === 1 ? "Last month" : `${diffMonths} months ago`;
  }

  const diffYears = Math.floor(diffDays / 365);
  return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`;
}
